import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs/operators';
import isEmail from 'validator/es/lib/isEmail';

import { AnalyticsService, MixpanelParamOverrides } from '@app/core/analytics.service';
import { ApiService } from '@app/core/api.service';
import { NavigatorService } from '@app/core/navigator.service';

import { PatientReferralGraphQL } from './patient-referral-graphql.service';

@Component({
  selector: 'om-invite-friends-modal',
  templateUrl: './invite-friends-modal.component.html',
  styleUrls: ['./invite-friends-modal.component.scss'],
})
export class InviteFriendsModalComponent implements OnInit, OnDestroy {
  showThankYou = false;
  invitePending = false;
  inviteeEmail: string;
  referralTitle: string;
  referralDescription: string;

  messageBody: string;
  primeButtonText = 'Copy link';
  isPrimeCopying = false;
  @Input() mixpanelParams: MixpanelParamOverrides = {};

  constructor(
    private analyticsService: AnalyticsService,
    private apiService: ApiService,
    private modal: NgbActiveModal,
    private navigatorService: NavigatorService,
    private patientReferralGraphQL: PatientReferralGraphQL,
  ) {}

  ngOnInit() {
    this.referralTitle = 'Invite your friends and family';
    this.referralDescription =
      'Be the one to introduce your loved ones to better healthcare.\n\nWith your invitation, the first year is $79 (down from $99) for Amazon Prime members, and $149 (down from $199) for everyone else.';

    this.patientReferralGraphQL
      .fetch()
      .pipe(take(1))
      .subscribe(result => {
        if (result.data?.patient?.referral) {
          this.messageBody = result.data.patient.referral.messageBody;
        }
      });
  }

  ngOnDestroy() {
    this.analyticsService.trackReferralModalClosed(this.mixpanelParams);
  }

  validateAndSubmit(event: Event, emailInput: HTMLInputElement) {
    event.preventDefault();

    if (this.validateEmail(emailInput)) {
      this.sendReferral(this.inviteeEmail);
    }
  }

  inviteMore() {
    this.showThankYou = false;

    this.analyticsService.trackInviteMoreFriends(this.mixpanelParams);
  }

  closeModal() {
    this.modal.close();
  }

  copyLink() {
    this.navigatorService.writeToClipboard(this.messageBody).then(() => {
      this.showCopySuccess();
      this.analyticsService.trackReferralLinkCopied(this.mixpanelParams);
    });
  }

  private showCopySuccess() {
    this.isPrimeCopying = true;

    setTimeout(() => {
      this.primeButtonText = 'Copied!';
    }, 100);

    setTimeout(() => {
      this.isPrimeCopying = false;
      this.primeButtonText = 'Copy link';
    }, 2000);
  }

  private validateEmail(element: HTMLInputElement): boolean {
    const email = element.value;

    if (isEmail(email)) {
      element.classList.remove('is-invalid');
      element.classList.add('is-valid');

      this.inviteeEmail = email;
      return true;
    } else {
      element.classList.remove('is-valid');
      element.classList.add('is-invalid');

      this.inviteeEmail = '';
      this.analyticsService.trackReferralSubmissionError(this.mixpanelParams);
      return false;
    }
  }

  private sendReferral(email: string) {
    this.invitePending = true;
    const requestPayload = this.formatParams(email);

    this.apiService.post('/api/v2/patient/referrals', requestPayload, true).subscribe(() => {
      this.analyticsService.trackReferralSubmitted(email, this.mixpanelParams);
      this.showThankYou = true;
      this.invitePending = false;
    });
  }

  private formatParams(email: string): object {
    return {
      referral: {
        emails: [
          {
            email: email,
            referral_type: 'friend',
          },
        ],
      },
    };
  }
}
